/**
 * collage-canvas.js – Smooth interactive collage canvas
 *
 * Pan: drag (all devices), scroll wheel (desktop)
 * Zoom: pinch (mobile), Ctrl/Cmd + scroll (desktop), controls
 * Momentum inertia after drag release
 * Subtle background parallax
 */

const MOBILE_BP = 750;
const DRAG_THRESH = 6;
const ZOOM_MIN = 0.2;
const ZOOM_MAX = 5;
const ZOOM_STEP = 0.25;
const ZOOM_WHEEL = 0.002;
const FRICTION = 0.95;
const VEL_STOP = 0.3;
const BG_PARALLAX = 0.03;
const VEL_WINDOW = 80;

class CollageCanvasComponent extends HTMLElement {
  #canvas = null;
  #inner = null;
  #bg = null;

  #x = 0;
  #y = 0;
  #scale = 1;

  #vx = 0;
  #vy = 0;

  #dragging = false;
  #anchorX = 0;
  #anchorY = 0;
  #downCX = 0;
  #downCY = 0;
  #downPointerId = 0;
  #moved = false;
  #trail = [];
  #dragAxis = null;

  #pinch = null;
  #currentAudio = null;
  #audioRaf = null;
  #globalMuted = false;
  #lazyObserver = null;
  #reducedMotion = false;
  #raf = null;
  #panelOpen = false;

  #onResize = () => {
    this.#clamp();
    this.#render();
  };

  /* ── Lifecycle ─────────────────────────────────────── */

  connectedCallback() {
    this.#canvas = this.querySelector('[ref="canvas"]');
    this.#inner = this.querySelector('[ref="canvasInner"]');
    this.#bg = this.querySelector('.cc__bg');
    if (!this.#canvas || !this.#inner) return;

    const mq = matchMedia('(prefers-reduced-motion: reduce)');
    this.#reducedMotion = mq.matches;
    mq.addEventListener('change', (e) => (this.#reducedMotion = e.matches));

    this.#bindPointer();
    this.#bindWheel();
    this.#bindTouch();
    this.#bindControls();
    this.#initLazyMedia();
    this.#initVideoMuteButtons();
    this.#initAudioPlayers();
    this.#initAudioWaveforms();
    this.#zoomFit();

    const reFit = () => this.#zoomFit();
    if (document.readyState === 'complete') {
      requestAnimationFrame(reFit);
    } else {
      window.addEventListener('load', reFit, { once: true });
    }

    window.addEventListener('resize', this.#onResize, { passive: true });
  }

  disconnectedCallback() {
    this.#lazyObserver?.disconnect();
    this.#lazyObserver = null;
    if (this.#raf) cancelAnimationFrame(this.#raf);
    this.#raf = null;
    if (this.#audioRaf) cancelAnimationFrame(this.#audioRaf);
    this.#audioRaf = null;
    window.removeEventListener('resize', this.#onResize);
  }

  get #isMobile() { return innerWidth < MOBILE_BP; }
  get #navMode() { return this.dataset.mobileNav || 'pan'; }
  get #diagonal() { return this.hasAttribute('data-pan-diagonal'); }
  get #active() {
    if (this.#reducedMotion) return false;
    if (this.#isMobile && this.#navMode === 'scroll') return false;
    return true;
  }

  /* ── Pointer drag ──────────────────────────────────── */

  #bindPointer() {
    const c = this.#canvas;
    c.addEventListener('pointerdown', (e) => this.#ptrDown(e));
    c.addEventListener('pointermove', (e) => this.#ptrMove(e));
    c.addEventListener('pointerup', (e) => this.#ptrUp(e));
    c.addEventListener('pointercancel', (e) => this.#ptrUp(e));
  }

  #ptrDown(e) {
    if (this.#pinch || !this.#active || e.button !== 0) return;
    this.#stopMomentum();
    this.#dragging = true;
    this.#moved = false;
    this.#dragAxis = null;
    this.#downCX = e.clientX;
    this.#downCY = e.clientY;
    this.#downPointerId = e.pointerId;
    this.#anchorX = e.clientX - this.#x;
    this.#anchorY = e.clientY - this.#y;
    this.#trail = [{ x: e.clientX, y: e.clientY, t: performance.now() }];
  }

  #ptrMove(e) {
    if (!this.#dragging || this.#pinch) return;
    const rawX = e.clientX - this.#anchorX;
    const rawY = e.clientY - this.#anchorY;

    if (!this.#moved) {
      if (Math.hypot(e.clientX - this.#downCX, e.clientY - this.#downCY) > DRAG_THRESH) {
        this.#moved = true;
        this.setAttribute('data-panning', '');
        try { this.#canvas.setPointerCapture(this.#downPointerId); } catch (_) {}
        if (!this.#diagonal) {
          this.#dragAxis =
            Math.abs(e.clientX - this.#downCX) >= Math.abs(e.clientY - this.#downCY) ? 'x' : 'y';
        }
      }
    }

    if (!this.#moved) return;

    if (this.#diagonal || !this.#dragAxis) {
      this.#x = rawX;
      this.#y = rawY;
    } else if (this.#dragAxis === 'x') {
      this.#x = rawX;
    } else {
      this.#y = rawY;
    }

    const now = performance.now();
    this.#trail.push({ x: e.clientX, y: e.clientY, t: now });
    while (this.#trail.length > 2 && now - this.#trail[0].t > VEL_WINDOW)
      this.#trail.shift();

    this.#clamp();
    this.#render();
  }

  #ptrUp(_e) {
    if (!this.#dragging) return;
    this.#dragging = false;
    this.removeAttribute('data-panning');

    if (this.#trail.length >= 2 && this.#moved) {
      const a = this.#trail[0];
      const b = this.#trail[this.#trail.length - 1];
      const dt = b.t - a.t;
      if (dt > 5 && dt < 300) {
        const frame = 16.67;
        this.#vx = ((b.x - a.x) / dt) * frame;
        this.#vy = ((b.y - a.y) / dt) * frame;
        if (this.#dragAxis === 'x') this.#vy = 0;
        if (this.#dragAxis === 'y') this.#vx = 0;
        this.#startMomentum();
      }
    }
    this.#trail = [];
    this.#dragAxis = null;
  }

  /* ── Wheel ─────────────────────────────────────────── */

  #bindWheel() {
    this.#canvas.addEventListener('wheel', (e) => this.#onWheel(e), { passive: false });
  }

  #onWheel(e) {
    if (!this.#active) return;
    e.preventDefault();
    let dx = e.deltaX;
    let dy = e.deltaY;
    if (e.deltaMode === 1) { dx *= 16; dy *= 16; }
    if (e.deltaMode === 2) { dx *= 100; dy *= 100; }

    if (e.ctrlKey || e.metaKey) {
      this.#zoomAt(e.clientX, e.clientY, -dy * ZOOM_WHEEL);
    } else {
      this.#stopMomentum();
      this.#x -= dx;
      this.#y -= dy;
      this.#clamp();
      this.#render();
    }
  }

  /* ── Touch (pinch zoom) ────────────────────────────── */

  #bindTouch() {
    this.#canvas.addEventListener('touchstart', (e) => {
      if (!this.#active || e.touches.length !== 2) return;
      this.#dragging = false;
      this.removeAttribute('data-panning');
      const [a, b] = [e.touches[0], e.touches[1]];
      this.#pinch = {
        d: Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY),
        cx: (a.clientX + b.clientX) / 2,
        cy: (a.clientY + b.clientY) / 2,
      };
    }, { passive: true });

    this.#canvas.addEventListener('touchmove', (e) => {
      if (!this.#pinch || e.touches.length !== 2) return;
      e.preventDefault();
      const [a, b] = [e.touches[0], e.touches[1]];
      const d = Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
      const cx = (a.clientX + b.clientX) / 2;
      const cy = (a.clientY + b.clientY) / 2;
      this.#zoomAt(cx, cy, (d - this.#pinch.d) / this.#pinch.d);
      this.#pinch = { d, cx, cy };
    }, { passive: false });

    this.#canvas.addEventListener('touchend', (e) => {
      if (e.touches.length < 2) this.#pinch = null;
    }, { passive: true });
  }

  /* ── Zoom ──────────────────────────────────────────── */

  #zoomAt(clientX, clientY, delta) {
    if (!this.#canvas || !this.#inner) return;
    const rect = this.#canvas.getBoundingClientRect();
    const px = clientX - rect.left;
    const py = clientY - rect.top;
    const prev = this.#scale;
    const next = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, prev * (1 + delta)));
    const r = next / prev;
    this.#x -= (px - this.#x) * (r - 1);
    this.#y -= (py - this.#y) * (r - 1);
    this.#scale = next;
    this.#clamp();
    this.#render();
    this.#updateLabel();
  }

  #zoomStep(step) {
    if (!this.#canvas) return;
    const r = this.#canvas.getBoundingClientRect();
    this.#zoomAt(r.left + r.width / 2, r.top + r.height / 2, step);
  }

  #zoomFit() {
    if (!this.#canvas || !this.#inner) return;
    this.#scale = this.#isMobile ? 0.25 : 0.35;
    this.#centerCanvas();
    this.#render();
    this.#updateLabel();
  }

  /* ── Momentum ──────────────────────────────────────── */

  #startMomentum() {
    if (this.#raf) return;
    this.#tick();
  }

  #stopMomentum() {
    if (this.#raf) { cancelAnimationFrame(this.#raf); this.#raf = null; }
    this.#vx = 0;
    this.#vy = 0;
  }

  #tick() {
    if (Math.abs(this.#vx) < VEL_STOP && Math.abs(this.#vy) < VEL_STOP) {
      this.#vx = 0;
      this.#vy = 0;
      this.#raf = null;
      return;
    }

    this.#x += this.#vx;
    this.#y += this.#vy;
    this.#vx *= FRICTION;
    this.#vy *= FRICTION;

    const [cx, cy] = this.#clampedXY();
    if (Math.abs(cx - this.#x) > 0.01) this.#vx = 0;
    if (Math.abs(cy - this.#y) > 0.01) this.#vy = 0;
    this.#x = cx;
    this.#y = cy;

    this.#render();
    this.#raf = requestAnimationFrame(() => this.#tick());
  }

  /* ── Bounds ────────────────────────────────────────── */

  #clampedXY() {
    if (!this.#canvas || !this.#inner) return [this.#x, this.#y];
    const sw = this.#inner.scrollWidth * this.#scale;
    const sh = this.#inner.scrollHeight * this.#scale;
    const vw = this.#canvas.clientWidth;
    const vh = this.#canvas.clientHeight;
    const mnX = Math.min(0, vw - sw), mxX = Math.max(0, vw - sw);
    const mnY = Math.min(0, vh - sh), mxY = Math.max(0, vh - sh);
    return [
      Math.max(mnX, Math.min(mxX, this.#x)),
      Math.max(mnY, Math.min(mxY, this.#y)),
    ];
  }

  #clamp() {
    [this.#x, this.#y] = this.#clampedXY();
  }

  #centerCanvas() {
    if (!this.#canvas || !this.#inner) return;
    const sw = this.#inner.scrollWidth * this.#scale;
    const sh = this.#inner.scrollHeight * this.#scale;
    const vw = this.#canvas.clientWidth;
    const vh = this.#canvas.clientHeight;
    this.#x = -(sw - vw) / 2;
    this.#y = -(sh - vh) / 2;
  }

  /* ── Render ────────────────────────────────────────── */

  #render() {
    if (!this.#inner) return;
    this.#inner.style.transform =
      `translate(${this.#x}px,${this.#y}px) scale(${this.#scale})`;

    if (this.#bg && this.#canvas) {
      const sw = this.#inner.scrollWidth * this.#scale;
      const sh = this.#inner.scrollHeight * this.#scale;
      const vw = this.#canvas.clientWidth;
      const vh = this.#canvas.clientHeight;
      const rx = sw > vw ? (sw - vw) / 2 : 1;
      const ry = sh > vh ? (sh - vh) / 2 : 1;
      const centerX = -(sw - vw) / 2;
      const centerY = -(sh - vh) / 2;
      const nx = rx > 1 ? (this.#x - centerX) / rx : 0;
      const ny = ry > 1 ? (this.#y - centerY) / ry : 0;
      this.#bg.style.transform =
        `translate3d(${nx * BG_PARALLAX * vw}px,${ny * BG_PARALLAX * vh}px,0)`;
    }
  }

  /* ── Controls ──────────────────────────────────────── */

  #bindControls() {
    this.querySelectorAll('[data-zoom]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const a = btn.dataset.zoom;
        if (a === 'in') this.#zoomStep(ZOOM_STEP);
        else if (a === 'out') this.#zoomStep(-ZOOM_STEP);
        else if (a === 'fit') this.#zoomFit();
      });
    });

    const muteBtn = this.querySelector('[data-action="mute"]');
    if (muteBtn) {
      muteBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.#globalMuted = !this.#globalMuted;
        muteBtn.dataset.muted = String(this.#globalMuted);
        this.#applyGlobalMute();
      });
    }
  }

  #applyGlobalMute() {
    if (this.#globalMuted) {
      this.#muteAllVideos();
      this.#stopAllAudio();
      this.#currentAudio = null;
    }
  }

  #updateLabel() {
    const el = this.querySelector('[ref="zoomLabel"]');
    if (el) el.textContent = `${Math.round(this.#scale * 100)}%`;
  }

  /* ── Lazy-load videos ──────────────────────────────── */

  #initLazyMedia() {
    const videos = this.querySelectorAll('video[data-src]');
    if (!videos.length) return;

    if (!('IntersectionObserver' in window)) {
      videos.forEach((v) => this.#loadVideo(v));
      return;
    }

    this.#lazyObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#loadVideo(entry.target);
            this.#lazyObserver?.unobserve(entry.target);
          }
        }
      },
      { root: this.#canvas, rootMargin: '200px', threshold: 0 }
    );

    videos.forEach((v) => this.#lazyObserver?.observe(v));
  }

  #loadVideo(video) {
    const src = video.dataset.src;
    if (!src) return;

    const sources = video.querySelectorAll('source[data-lazy-src]');
    if (sources.length) {
      sources.forEach((s) => {
        const lazySrc = s.getAttribute('data-lazy-src');
        if (lazySrc) s.setAttribute('src', lazySrc);
      });
    } else {
      const source = document.createElement('source');
      source.src = src;
      source.type = 'video/mp4';
      video.appendChild(source);
    }

    video.load();
    if (!this.#reducedMotion) video.play().catch(() => {});
  }

  /* ── Video mute buttons ────────────────────────────── */

  #initVideoMuteButtons() {
    this.querySelectorAll('.cc-item__video-mute').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (this.#moved) return;
        const item = btn.closest('.cc-item');
        const video = item?.querySelector('video');
        if (!video) return;
        const willUnmute = video.muted;
        if (willUnmute) {
          this.#stopAllAudio();
          this.querySelectorAll('video').forEach((v) => { if (v !== video) v.muted = true; });
          this.querySelectorAll('.cc-item__video-mute').forEach((b) => {
            if (b !== btn) b.dataset.muted = 'true';
          });
        }
        video.muted = !willUnmute;
        btn.dataset.muted = String(!willUnmute);
        btn.setAttribute('aria-label', !willUnmute ? 'Unmute' : 'Mute');
      });
    });
  }

  /* ── Audio players (WhatsApp-style) ────────────────── */

  #initAudioPlayers() {
    this.querySelectorAll('.cc-item__audio').forEach((wrapper) => {
      const playBtn = wrapper.querySelector('.cc-audio__play');
      const track = wrapper.querySelector('.cc-audio__track');
      const timeEl = wrapper.querySelector('[ref="audioTime"]');
      const src = wrapper.dataset?.audioSrc;
      if (!playBtn || !src) return;

      playBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (this.#moved) return;
        if (this.#globalMuted) return;
        this.#toggleAudio(wrapper, src, playBtn, track, timeEl);
      });

      if (track) {
        track.addEventListener('click', (e) => {
          e.stopPropagation();
          if (this.#moved) return;
          if (!this.#currentAudio || wrapper.getAttribute('data-playing') !== 'true') return;
          const rect = track.getBoundingClientRect();
          const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
          if (this.#currentAudio.duration) {
            this.#currentAudio.currentTime = pct * this.#currentAudio.duration;
            this.#updateAudioBars(track, pct);
          }
        });
      }
    });
  }

  #stopAllAudio() {
    if (this.#currentAudio && !this.#currentAudio.paused) {
      this.#currentAudio.pause();
    }
    const playing = this.querySelector('.cc-item__audio[data-playing="true"]');
    if (playing) {
      playing.removeAttribute('data-playing');
      playing.querySelector('.cc-audio__play')?.setAttribute('aria-pressed', 'false');
    }
    this.#stopAudioProgress();
  }

  #muteAllVideos() {
    this.querySelectorAll('video').forEach((v) => { v.muted = true; });
    this.querySelectorAll('.cc-item__video-mute').forEach((b) => { b.dataset.muted = 'true'; });
  }

  #toggleAudio(wrapper, src, playBtn, track, timeEl) {
    if (this.#currentAudio && !this.#currentAudio.paused) {
      this.#currentAudio.pause();
      const prev = this.querySelector('.cc-item__audio[data-playing="true"]');
      if (prev) {
        prev.removeAttribute('data-playing');
        prev.querySelector('.cc-audio__play')?.setAttribute('aria-pressed', 'false');
      }
      this.#stopAudioProgress();

      if (this.#currentAudio._ccSrc === src) {
        this.#currentAudio = null;
        return;
      }
    }

    if (!this.#currentAudio || this.#currentAudio._ccSrc !== src) {
      this.#currentAudio = new Audio(src);
      this.#currentAudio._ccSrc = src;
    }

    this.#muteAllVideos();
    this.#currentAudio.play().then(() => {
      wrapper.setAttribute('data-playing', 'true');
      playBtn.setAttribute('aria-pressed', 'true');
      this.#startAudioProgress(wrapper, track, timeEl);
    }).catch(() => {});

    this.#currentAudio.addEventListener('ended', () => {
      wrapper.removeAttribute('data-playing');
      playBtn.setAttribute('aria-pressed', 'false');
      this.#stopAudioProgress();
      this.#updateAudioBars(track, 0);
      if (timeEl) timeEl.textContent = this.#fmtTime(this.#currentAudio?.duration || 0);
      this.#currentAudio = null;
    }, { once: true });
  }

  #startAudioProgress(wrapper, track, timeEl) {
    this.#stopAudioProgress();
    const update = () => {
      if (!this.#currentAudio) return;
      const dur = this.#currentAudio.duration || 1;
      const pct = this.#currentAudio.currentTime / dur;
      this.#updateAudioBars(track, pct);
      if (timeEl) timeEl.textContent = this.#fmtTime(this.#currentAudio.currentTime);
      this.#audioRaf = requestAnimationFrame(update);
    };
    this.#audioRaf = requestAnimationFrame(update);
  }

  #stopAudioProgress() {
    if (this.#audioRaf) { cancelAnimationFrame(this.#audioRaf); this.#audioRaf = null; }
  }

  #updateAudioBars(track, pct) {
    if (!track) return;
    const bars = track.querySelectorAll('.cc-waveform__bar');
    const played = Math.floor(pct * bars.length);
    bars.forEach((bar, i) => {
      bar.classList.toggle('cc-waveform__bar--played', i < played);
    });
  }

  #fmtTime(s) {
    if (!s || !isFinite(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  }

  /* ── Audio waveforms ───────────────────────────────── */

  #initAudioWaveforms() {
    this.querySelectorAll('.cc-item__audio').forEach((wrapper) => {
      const src = wrapper.dataset?.audioSrc;
      const track = wrapper.querySelector('.cc-audio__track');
      if (!track || !src) return;
      this.#drawWaveformBars(track, src).catch(() => {
        this.#drawDecorativeWaveform(track);
      });
    });
  }

  async #drawWaveformBars(container, src) {
    const res = await fetch(src, { mode: 'cors' });
    const buf = await res.arrayBuffer();
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) throw new Error('No AudioContext');
    const ctx = new Ctx();
    const data = await ctx.decodeAudioData(buf);
    await ctx.close();

    const left = data.getChannelData(0);
    const barCount = Math.min(60, Math.max(30, Math.floor(container.offsetWidth / 5)));
    const step = Math.floor(left.length / barCount);
    const bars = [];

    for (let i = 0; i < barCount; i++) {
      let sum = 0;
      for (let j = 0; j < step; j++) {
        sum += Math.abs(left[i * step + j] || 0);
      }
      bars.push((sum / step) * 100);
    }

    const max = Math.max(...bars, 1);
    container.innerHTML = bars
      .map((v) => `<span class="cc-waveform__bar" style="--h: ${Math.max(15, (v / max) * 100)}%"></span>`)
      .join('');
  }

  #drawDecorativeWaveform(container) {
    const barCount = 50;
    const bars = [];
    for (let i = 0; i < barCount; i++) {
      const base = 25 + Math.sin(i * 0.4) * 20;
      bars.push(Math.max(15, Math.min(95, base + (Math.random() - 0.5) * 40)));
    }
    container.innerHTML = bars
      .map((v) => `<span class="cc-waveform__bar" style="--h: ${Math.round(v)}%"></span>`)
      .join('');
  }
}

if (!customElements.get('collage-canvas-component')) {
  customElements.define('collage-canvas-component', CollageCanvasComponent);
}
